import React, { useContext } from "react";
import { Formik, Form, Field } from "formik";
import ModalComponent from "../../../../components/Modal";
import TextInput from "../../../../components/TextInput";
import { AddEmployeeSchema } from "../../../../components/Helpers";
import { EmployeeContext } from "../../../../Context/Employee";
import { toast } from "react-toastify";
import CustomDropdown from "../../../../components/CustomDropdown";

const AddEmployeeModal = ({
  onClose,
  show,
  title,
  initialValues,
  refreshEmployees,
}) => {
  const { addEmployee } = useContext(EmployeeContext);
  
  // Set default values if not provided, and force gender/status defaults if missing or empty
  const defaultValues = {
    firstName: "",
    lastName: "",
    gender: "M",
    email: "",
    phoneNumber: "",
    technology: "",
    status: "A",
    company: "",
    ...(initialValues || {})
  };
  if (!defaultValues.gender) defaultValues.gender = "M";
  if (!defaultValues.status) defaultValues.status = "A";

  return (
    <ModalComponent show={show} onClose={onClose} title={title}>
      <Formik
        initialValues={defaultValues}
        validationSchema={AddEmployeeSchema}
        enableReinitialize
        
        onSubmit={async (values, { resetForm, setSubmitting }) => {
          console.log("Form values:", values);
          // Debug log for status and gender
          console.log("Selected status:", values.status); // Should be 'A' for Active
          console.log("Selected gender:", values.gender); // Should be 'M' for Male
          try {
            const payload = {
              fname: values.firstName,
              lname: values.lastName,
              gender: values.gender,
              email: values.email,
              phone: values.phoneNumber,
              technology: values.technology,
              stsCd: values.status,
              company: values.company,
            };

            await addEmployee(payload);
            console.log("Employee added successfully:", payload);
            toast.success("User created successfully");
            resetForm();

            if (typeof refreshEmployees === "function") {
              refreshEmployees();
            }

            onClose();
          } catch (error) {
            let errorMessage = "Email already exists. Please use a different email address.";
            if (error && error.response && error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            }
            toast.error(errorMessage);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, handleChange, values, isSubmitting,setFieldValue }) => (
          <Form className="max-h-[85vh] overflow-y-auto">
            <div className="mt-4" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <TextInput
                  value={values?.firstName}
                  name="firstName"
                  placeholder="First Name"
                  handleChange={handleChange}
                  type="text"
                  classes="w-full"
                  legend
                />
                {errors.firstName && touched.firstName && (
                  <div className="text-red-500 text-sm">{errors.firstName}</div>
                )}
              </div>
              {/* Last Name */}
              <div>
                <TextInput
                  value={values?.lastName}
                  name="lastName"
                  placeholder="Last Name"
                  handleChange={handleChange}
                  type="text"
                  classes="w-full"
                  legend
                />
                {errors.lastName && touched.lastName && (
                  <div className="text-red-500 text-sm">{errors.lastName}</div>
                )}
              </div>
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                {/* Gender */}
                <div className="flex items-center space-x-4">
                  <label className="text-slate-400 text-base font-medium">
                    Gender:
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="gender"
                      value="M"
                      checked={values.gender === "M"}
                    />
                    <span>Male</span>
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="gender"
                      value="F"
                      checked={values.gender === "F"}
                    />
                    <span>Female</span>
                  </label>
                </div>
                {/* Status */}
                <div className="flex items-center space-x-4">
                  <label className="text-slate-400 text-base font-medium">
                    Status:
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="status"
                      value="A"
                      checked={values.status === "A"}
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center space-x-2 text-lg">
                    <Field
                      type="radio"
                      name="status"
                      value="D"
                      checked={values.status === "D"}
                    />
                    <span>Deactive</span>
                  </label>
                </div>
              </div>
              {/* Email */}
              <div>
                <TextInput
                  value={values?.email}
                  name="email"
                  placeholder="Email"
                  handleChange={handleChange}
                  type="email"
                  classes="w-full"
                  legend
                />
                {errors.email && touched.email && (
                  <div className="text-red-500 text-sm">{errors.email}</div>
                )}
              </div>
              {/* Phone Number */}
              <div>
                <TextInput
                  value={values?.phoneNumber}
                  name="phoneNumber"
                  placeholder="Phone Number"
                  handleChange={e => {
                    const allowed = e.target.value.replace(/[^\d\+\-\(\)\s]/g, "").slice(0, 13);
                    setFieldValue("phoneNumber", allowed);
                  }}
                  type="text"
                  classes="w-full"
                  legend
                />
                {/* Show phone validation error live while typing */}
                {errors.phoneNumber && (
                  <div className="text-red-500 text-sm">
                    {errors.phoneNumber}
                  </div>
                )}
              </div>
              {/* Technology */}
              <div>
                <TextInput
                  value={values?.technology}
                  name="technology"
                  placeholder="Technology"
                  handleChange={handleChange}
                  type="text"
                  classes="w-full"
                  legend
                />
                {errors.technology && touched.technology && (
                  <div className="text-red-500 text-sm">
                    {errors.technology}
                  </div>
                )}
              </div>
              <div>
                {/* <CustomDropdown
                  name="company"
                  options={[
                    { label: "DATACLOUD", value: "DATACLOUD" },
                    { label: "UNISOFT", value: "UNISOFT" },
                  ]}
                  value={values?.company}
                  onChange={(selectedOption) =>
                    handleChange({
                      target: {
                        name: "company",
                        value: selectedOption?.value,
                      },
                    })
                  }
                  placeholder="Select Company"
                  legend
                  classes="w-full"
                /> */}
                <CustomDropdown
                value={
                  [
                    { label: "DATACLOUD", value: "DATACLOUD" },
                    { label: "UNISOFT", value: "UNISOFT" },
                  ].find((user) => user?.value === values?.company) || null
                }
                name="company"
                handleChange={(selectedOption) => {
                  console.log(selectedOption,"OPTION")
                  // dispatch(setResetStateTimesheetById());
                  // dispatch(requestProjectsById(selectedOption?.id));
                  setFieldValue("company", selectedOption.value);
                  // setSelectedEmployeeId(selectedOption?.id);
                }}
                customClasses="w-full text-left"
                placeholder="Select Company"
                options={[
                  { label: "DATACLOUD", value: "DATACLOUD" },
                  { label: "UNISOFT", value: "UNISOFT" },
                ]}
                handleGetOptionValue={(option) => option.value}
                handleGetOptionName={(option) => option.label}
              />
                {errors.company && touched.company && (
                  <div className="text-red-500 text-sm">{errors.company}</div>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 mt-6">
              <button
                type="button"
                className="bg-gray-300 text-black h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-gray-400"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-primary text-white h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-primaryDark"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalComponent>
  );
};

export default AddEmployeeModal;
